import * as React from 'react';
import { useState } from 'react';
import { clsx } from 'clsx';
import { ApiError, apiPost, logApiError } from "../api";
import { useContext, useEffect } from 'react';
import Dump from "../components/dump";
import Spinner, { SpinnerColorsEnum } from "../components/spinner";
import WebcamCapture from "../components/webcamCapture";
import { isFunction } from "../helpers/utils";
import { fetchContact, fetchCountries, stepBack } from "../loaders";
import ColumnMain from "../src/columnMain";
import ColumnSide from "../src/columnSide";
import Layout from "../src/layout";
import Summary from "../src/summary";
import { ContextSession } from "./app";
import { useForm, Controller, type SubmitHandler } from 'react-hook-form';
import HookFormDevToolLazy from "../../js/components/hookFormDevToolLazy";
import { extractIdFromHydraId, formatDate, inputClass, inputRadioClass, isSubscription, parseStartDate } from "./utils";
import { PhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';
import { ErrorMessage } from "@hookform/error-message";
import FieldError from "../components/fieldError";
import LockedNotice from "../components/lockedNotice";
import { DiscountBanner, useAvailableDiscount } from "../components/discount";

type Props = Readonly<{
	setState: Function,
	offer?: {},
	memberRegistrationUrl?: string | null,
	memberLoginUrl?: string | null,
	passwordProfile?: string | null,
}>

type PasswordProfile = 'alphanumeric' | 'strong';

/*
	The two Resamania environments enforce contradictory password rules and the
	API exposes no way to read them: sandbox (staging) rejects special characters,
	production requires one. The server derives the profile from RESA_ENV and
	passes it in, so this same build validates correctly whichever environment we
	point at — testing against sandbox and working in production stop being
	mutually exclusive.

	The 'strong' pattern only requires *a* special character rather than a fixed
	set, because we have not enumerated exactly which specials production accepts.
	The submit handler surfaces production's own password error as the safety net
	for anything that slips past this.
*/
const PASSWORD_RULES: Record<PasswordProfile, { pattern: RegExp; help: string }> = {
	alphanumeric: {
		pattern: /^(?=.*[A-Z])(?=.*[a-z])(?=.*[0-9])[A-Za-z0-9]{8,}$/,
		help: 'At least 8 characters, with a capital letter, a lower case letter and a number. Letters and numbers only — no spaces or special characters.',
	},
	strong: {
		pattern: /^(?=.*[A-Z])(?=.*[a-z])(?=.*[0-9])(?=.*[^A-Za-z0-9]).{8,}$/,
		help: 'At least 8 characters, with a capital letter, a lower case letter, a number and a special character (for example ! @ # $).',
	},
};

// Unknown or missing profile falls back to the strict rule — the same safe-for-
// production default the server uses.
function resolvePasswordRule(profile?: string | null): { pattern: RegExp; help: string } {
	return profile === 'alphanumeric' ? PASSWORD_RULES.alphanumeric : PASSWORD_RULES.strong;
}

type ContactFormValues = {
	givenName: string,
	familyName: string,
	birthDate: string,
	email: string,
	initialPassword: string,
	mobile: string,
	gender: string,

	addressCountryIso: string, //"FR",
	addressLocality: string,
	streetAddress: string, //"52 chemin du test",
	postalCode: string, //"83390",

	consentTerms: boolean,

	news: boolean,
	advertising: boolean,
	partners: boolean,

	photo: string,
}

/*
	react-international-phone hands back a formatted value — e.g. "+44 7944 654015", or
	"+44 0794 4654015" when the customer includes the UK trunk 0. Strip the formatting to
	E.164 digits and drop a trunk 0 straight after the +44 dial code, so "07944…" and
	"7944…" both become a valid number the signature SMS can reach. Applied on every
	keystroke so the stored value is always clean (the leading 0 just bounces off the field).
*/
function normaliseUkMobile(value: string | null | undefined): string {
	return (value ?? '').replace(/[^\d+]/g, '').replace(/^\+440/, '+44');
}

/*
	Small all-caps "eyebrow" label sitting above each field. Every input on this
	form previously relied on its placeholder as its label, which disappears on
	input and gives the native date field (which cannot show placeholder text) no
	visible caption at all. The required marker lives here rather than in the
	placeholder so it shows on those fields too.
*/
function FieldLabel({htmlFor, required = false, children}: Readonly<{htmlFor: string, required?: boolean, children: React.ReactNode}>) {
	return (
		<label htmlFor={htmlFor} className="block text-[11px] font-bold uppercase tracking-wider text-[#737373] mb-1">
			{children}{required ? <span className="text-red-500" aria-hidden="true"> *</span> : null}
		</label>
	);
}

function Contact({setState, offer, memberRegistrationUrl, memberLoginUrl, passwordProfile, ...otherProps}: Props) {

	const state = useContext(ContextSession);
	const [countries, setCountries] = useState(null);

	// Keep the club's discount in view on the details step too.
	const discount = useAvailableDiscount(state.hash, isSubscription(offer));

	// Past the point of no return: a mandate or payable exists, so these details
	// (and the start date) are fixed on Resamania's side. The form is shown
	// read-only and the customer is offered a restart rather than an edit.
	const committed = Boolean(state?.mandateId || state?.payableId);

	const passwordRule = resolvePasswordRule(passwordProfile);

	// True once the email is found to belong to an existing member. We cannot
	// verify ownership (see the server-side note), so instead of continuing we
	// hand them off to the Resamania members' site to finish there.
	const [existingMember, setExistingMember] = useState(false);

	useEffect(() => {
		fetchCountries(state.hash).then((data) => {
			if (data) {
				setCountries(data);
			}
		});
	}, [state.hash]);

	let birthDateMax = new Date();
	birthDateMax.setFullYear(birthDateMax.getFullYear() - 10);
	birthDateMax.setDate(birthDateMax.getDate() + 1);

	const {
		register,
		handleSubmit,
		formState: { errors, isSubmitting },
		control,
		reset,
		watch,
		setError
	} = useForm<ContactFormValues>({ defaultValues: {
			// givenName: 'Adam',
			// familyName: 'Testa',
			// email: 'fafds@gfgsfgd.pl',
			// mobile: '+48 690 577 251',
			// birthDate: formatDate(birthDateMax),
			// gender: "female",
			consentTerms: state.contactId !== "" ? true : false,
			// initialPassword: "Password1",
			//
			// //postalCode: '23100',
			// postalCode: 'OX449DA',
			addressCountryIso: 'GB',
			// addressLocality: 'Oxford',
			// streetAddress: "fsddfdasdf",
		}});

	const pictureId = watch("pictureId");

	const onSubmit: SubmitHandler<ContactFormValues> = (data) => {
		// console.log(data);

		const url = '/resa/contact/' + state?.hash;

		// The field is already normalised on change; normalise again defensively in case a
		// value ever reaches here another way.
		const mobile = normaliseUkMobile(data?.mobile);

		const toSend = {
			...data,
			mobile: mobile,
			birthDate: formatDate(new Date(data?.birthDate)),
			address: {
				"addressCountryIso": data?.addressCountryIso,
				"addressLocality": data?.addressLocality,
				"streetAddress": data?.streetAddress,
				"postalCode": data?.postalCode.replaceAll(' ', ''), //remove spaces from post code
			},
			pictureAllowed: true,
		};

		setExistingMember(false);

		return apiPost(url, toSend).then((response) => {
			if (isFunction(setState)) {
				// The email belongs to an existing member. We cannot verify ownership,
				// so we do not continue the signup here — we point them to the
				// Resamania members' site, where they can finish as a returning member.
				if (response.data === "Email already exists") {
					setError("email", { type: "manual" });
					setError("root.serverError", {
						type: "manual",
						message: 'We already have an account with that email address. Please continue on our members’ site to finish signing up.'
					});
					setExistingMember(true);
				} else {
					setState(response.data);
				}
			}
		}).catch(function (error) {
			if (error instanceof ApiError) {
				// Safety net: the environment can reject the password even after it
				// passes our pattern — for instance a special character this
				// environment does not accept, which we have not fully enumerated.
				// Surface it on the password field with the rule, not a generic error.
				const violations = error.data?.data?.violations;
				const passwordRejected = Array.isArray(violations)
					&& violations.some((v: any) => v?.propertyPath === 'initialPassword');

				if (passwordRejected) {
					setError("initialPassword", { type: "manual" });
					setError("root.serverError", {
						type: "manual",
						message: 'That password wasn’t accepted. ' + passwordRule.help
					});
				} else {
					setError("root.serverError", {
						type: String(error.status),
						message: 'Error has occurred. Check your entered data and try again.'
					});
				}
				logApiError(error);
			}
		});
	};

	useEffect(() => {
		fetchContact(state?.hash).then((data) => {
			if (data) {
				reset({
					...data,
					// birthDate: new Date(data?.birthDate),
					// Default the country to the UK rather than the alphabetical first
					// option. This reset runs on mount even for a new signup (where
					// data has no country), which otherwise wiped the 'GB' default and
					// left the select on Afghanistan. An existing customer's country
					// still wins.
					addressCountryIso: data?.addressCountryIso || 'GB',
					addressCountryCode: data?.address?.addressCountryCode
				});
			}
		});
	}, []);

	return (<>
		<Layout name={"YOUR DETAILS"}>
			<ColumnMain banner={discount ? <DiscountBanner discount={discount}/> : null}>
				{countries === null ? <div className="max-w-screen-xl mx-auto text-center px-4 sm:px-8 my-32"><Spinner color={SpinnerColorsEnum.Black}/></div> :
				<div className="relative">
				{/* Once committed the whole form is inert and the overlay below fades it. */}
				<form onSubmit={handleSubmit(onSubmit)} inert={committed || undefined}>
					<HookFormDevToolLazy control={control}/>
					<h3>CREATE YOUR MEMBER ACCOUNT</h3>
					<h5 className="mb-8">PERSONAL DETAILS</h5>

					<div className="flex flex-wrap gap-x-8">
						<div className="grow">
							<FieldLabel htmlFor="givenName" required>First name</FieldLabel>
							<input
								id="givenName"
								type="text"
								placeholder="e.g. Jane"
								className={clsx('w-full', inputClass(errors.givenName))}
								{...register("givenName", {required: true})}
							/>
							<FieldError error={errors.givenName} message="Enter your first name"/>
						</div>
						<div className="grow">
							<FieldLabel htmlFor="familyName" required>Last name</FieldLabel>
							<input
								id="familyName"
								type="text"
								placeholder="e.g. Smith"
								className={clsx('w-full', inputClass(errors.familyName))}
								{...register("familyName", {required: true})}
							/>
							<FieldError error={errors.familyName} message="Enter your last name"/>
						</div>
					</div>

					<div className="flex flex-wrap -mx-4">
						<div className="w-full xl:w-1/2 px-4">
							<FieldLabel htmlFor="birthDate" required>Date of birth</FieldLabel>
							<Controller
								name="birthDate"
								control={control}
								defaultValue=""
								rules={{ required: true }}
								render={({ field }) => (
									/*
										A native date input rather than a calendar popover.

										The popover put the input inside a PopoverButton and set
										tabIndex={-1}, so the field was unreachable by keyboard
										entirely — a required field on a purchase flow that only
										worked with a mouse.

										type="date" is keyboard operable and screen-reader labelled
										for free, displays in the visitor's own locale (so UK users
										see DD/MM/YYYY) while the value stays yyyy-MM-dd exactly as
										the API wants, and lets people type a date of birth rather
										than paging a calendar back forty years.
									*/
									<input
										{...field}
										id="birthDate"
										type="date"
										className={clsx('w-full', inputClass(errors.birthDate))}
										max={formatDate(birthDateMax)}
										min="1900-01-01"
									/>
								)}
							/>
							<FieldError error={errors.birthDate} message="Enter your date of birth"/>
						</div>

						<div className="w-full xl:w-1/2 px-4">
						<FieldLabel htmlFor="mobile" required>Mobile number</FieldLabel>
						<Controller
							name="mobile"
							control={control}
							defaultValue=""
							rules={{ required: true, minLength: 8 }}
							render={({ field }) => (
								<PhoneInput
									{...field}
									inputProps={{ id: 'mobile' }}
									className={clsx('w-full', inputClass(errors.mobile))}
									forceDialCode={true}
									defaultCountry="gb"
									allowMaskOverflow={true}
									value={field.value}
									onChange={(value) => field.onChange(normaliseUkMobile(value))}
								/>
							)}
						/>
						<FieldError error={errors.mobile} message="Enter your mobile number"/>
						</div>
					</div>

					<FieldLabel htmlFor="email" required>Email address</FieldLabel>
					<input
						id="email"
						type="email"
						placeholder="e.g. jane@example.com"
						className={clsx('w-full', inputClass(errors.email))}
						{...register("email", {
							required: true,
							// Require a plausible address — something@something.tld — rather
							// than just any non-empty string. type="email" only validates on
							// native submit, which react-hook-form bypasses, so "6666666"
							// was passing. Deliberately lenient: the real check is the API.
							pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/
						})}
					/>
					<FieldError error={errors.email} message="Enter a valid email address"/>

					{state?.contactId ? null : <div className="flex flex-wrap -mx-4">
						<div className="px-4 w-full md:w-1/2">
							<FieldLabel htmlFor="initialPassword" required>Password</FieldLabel>
							<input
								id="initialPassword"
								type="password"
								className={clsx('w-full', inputClass(errors.initialPassword))}
								{...register("initialPassword", {
									required: true,
									minLength: 8,
									pattern: passwordRule.pattern
								})}
							/>
							<FieldError error={errors.initialPassword} message="Enter a password that meets the requirements"/>
						</div>
						<div className="px-4 w-full md:w-1/2 text-grey-600 text-xs mb-5">
							{passwordRule.help}
						</div>
					</div>}

					<div className="text-[11px] font-bold uppercase tracking-wider text-[#737373] mb-1">Gender<span className="text-red-500" aria-hidden="true"> *</span></div>
					<div className={clsx('border px-2 -mx-2 flex py-1 gap-x-3 items-start mb-6', inputRadioClass(errors.gender))}>
						<input
							className="mt-1 mr-0 !mb-0 !focus:outline-0"
							id="genderMale"
							type="radio"
							value="male"
							{...register("gender", {required: true})}
						/>
						<label className="mb-0 inline-block align-middle" htmlFor="genderMale">
							Male
						</label>

						<input
							className="mt-1 mr-0 !mb-0 !focus:outline-0"
							id="genderFemale"
							type="radio"
							value="female"
							{...register("gender", {required: true})}
						/>
						<label className="mb-0 inline-block align-middle" htmlFor="genderFemale">
							Female
						</label>
					</div>
					<FieldError error={errors.gender} message="Select your gender"/>

					<hr/>

					<div className="flex flex-wrap xl:flex-nowrap gap-8 xl:gap-0">
						<div className="grow">
							<h5 className="mb-0">PHOTO</h5>
							{pictureId === null || pictureId === undefined ? <p className="mt-8 mb-0">
								Enable your device’s camera to take a verification photo. This will be used to identify
								you upon access to the gym building.
							</p> : null}
						</div>

						{pictureId === null || pictureId === undefined ?
							<div>
								<Controller
									name="photo"
									control={control}
									rules={{required: true}}
									render={({field: {onChange}}) => {
										return (
											<WebcamCapture
												className={clsx("bg-white p-2 max-w-80 shadow-2xl", inputClass(errors.photo))}
												onImageCapture={(dataURI) => {
													if (dataURI) {
														let image = dataURI?.split(',')[1];
														onChange(image);
													}
												}}
												screenshotFormat="image/jpeg"
											/>
										);
									}}
								/>
							</div>
							:
							<div className="mx-auto bg-white p-2 max-w-80 shadow-2xl">
								<iframe
									className="bg-primary-200 relative w-[304px] h-[228px]"
									src={"/resa/contact/photo/" + extractIdFromHydraId(pictureId) + '/' + state.hash}
									title="Photo"
								/>
							</div>
						}
					</div>

					<hr/>

					<h5 className="mb-8">ADDRESS</h5>

					<FieldLabel htmlFor="streetAddress" required>Address</FieldLabel>
					<input
						id="streetAddress"
						type="text"
						placeholder="e.g. 12 High Street"
						className={clsx('w-full', inputClass(errors.streetAddress))}
						{...register("streetAddress", {required: true})}
					/>
					<FieldError error={errors.streetAddress} message="Enter your address"/>

					<div className="flex flex-wrap gap-x-8">
						<div className="grow">
							<FieldLabel htmlFor="addressLocality" required>County</FieldLabel>
							<input
								id="addressLocality"
								type="text"
								placeholder="e.g. Oxfordshire"
								className={clsx('w-full', inputClass(errors.addressLocality))}
								{...register("addressLocality", {required: true})}
							/>
							<FieldError error={errors.addressLocality} message="Enter your county"/>
						</div>
						<div className="grow">
							<FieldLabel htmlFor="addressCountryIso">Country</FieldLabel>
							<select
								id="addressCountryIso"
								className={clsx('w-full max-w-full', inputClass(errors.addressCountryIso))}
								{...register("addressCountryIso")}
							>
								{countries?.map((country, index) => {
									return <option key={"country" + index} value={country?.iso}>{country?.name}</option>
								})}
							</select>
						</div>
					</div>

					<div className="flex flex-wrap gap-x-8">
						<div className="max-w-[307px] grow">
							<FieldLabel htmlFor="postalCode" required>Postcode</FieldLabel>
							<input
								id="postalCode"
								type="text"
								placeholder="e.g. OX44 9DA"
								className={clsx('w-full mb-0', inputClass(errors.postalCode))}
								{...register("postalCode", {
									required: true,
									pattern: /^([A-Za-z][A-Ha-hJ-Yj-y]?[0-9][A-Za-z0-9]? ?[0-9][A-Za-z]{2}|[Gg][Ii][Rr] ?0[Aa]{2})$/
								})}
							/>
							<FieldError error={errors.postalCode} message="Enter a valid postcode"/>
						</div>
					</div>

					<hr/>

					<h5 className="mb-8">COMMUNICATIONS</h5>

					<div
						className={clsx('border px-2 -mx-2 flex py-1 gap-x-3 items-start mb-4 lg:mb-6', inputRadioClass(errors.optInNotifications))}>
						<input
							className="mt-0.5 mr-0 !mb-0 !focus:outline-0"
							id="news"
							type="checkbox"
							//checked
							{...register("news", {required: false})}
						/>
						<label className="mb-0 text-xs inline-block align-middle" htmlFor="news">
							I want to receive notifications from the app (booking, etc.)
						</label>
					</div>

					<div
						className={clsx('border px-2 -mx-2 flex py-1 gap-x-3 items-start mb-4 lg:mb-6', inputRadioClass(errors.optInMarketing))}>
						<input
							className="mt-0.5 mr-0 !mb-0 !focus:outline-0"
							id="advertising"
							type="checkbox"
							//checked
							{...register("advertising", {required: false})}
						/>
						<label className="mb-0 text-xs inline-block align-middle" htmlFor="advertising">
							I AGREE to receive commercial communications, emails, telephone calls and others in order to
							provide relevant information about products and services, promotions, events or other related
							activities. I can revoke this consent at any time.
						</label>
					</div>

					<div
						className={clsx('border px-2 -mx-2 flex py-1 gap-x-3 items-start mb-4 lg:mb-6', inputRadioClass(errors.optInPartners))}>
						<input
							className="mt-0.5 mr-0 !mb-0 !focus:outline-0"
							id="partners"
							type="checkbox"
							//checked
							{...register("partners", {required: false})}
						/>
						<label className="mb-0 text-xs inline-block align-middle" htmlFor="partners">
							I AGREE to the sending of commercial communications from third party companies with which
							collaboration agreements have been concluded for the benefit of clients.
						</label>
					</div>

					<hr/>

					<h5 className="mb-8">DECLARATIONS</h5>

					<div className={clsx('border px-2 -mx-2 flex py-1 gap-x-3 items-start mb-4 lg:mb-6', inputRadioClass(errors.consentTerms))}>
						<input
							className="mt-0.5 mr-0 !mb-0 !focus:outline-0"
							id="consentTerms"
							type="checkbox"
							//checked
							{...register("consentTerms", {required: true})}
						/>
						<label className="mb-0 text-xs inline-block align-middle" htmlFor="consentTerms">
							By ticking this box, I accept and acknowledge that I have read the <a href="https://www.feelfitgym.com/terms-conditions/" target="_blank">General Terms and Conditions</a>
						</label>
					</div>
					<FieldError error={errors.consentTerms} message="You must accept the terms and conditions to continue"/>

					<hr className="my-8"/>

					<div className="text-red-400 mb-8">
						<ErrorMessage errors={errors} name={'root.serverError'}/>
					</div>

					{existingMember && memberRegistrationUrl &&
						<a href={memberRegistrationUrl} className="button !mb-8 !flex items-center justify-center">
							CONTINUE ON THE MEMBERS’ SITE
						</a>
					}

					<div className="flex flex-wrap gap-x-4 sm:gap-x-8 gap-y-4 justify-center">
						<button className="grey !mb-0" onClick={(event) => {
							// console.log('step back');
							event.preventDefault();
							stepBack(state.hash, setState);
						}}>BACK</button>

						<button disabled={isSubmitting} className="mb-0 flex items-center justify-center" type="submit">
							NEXT
							{isSubmitting && <Spinner color={SpinnerColorsEnum.Black} className="!w-4 !h-4"/>}
						</button>
					</div>
				</form>
				{committed && <LockedNotice hash={state.hash} setState={setState} memberLoginUrl={memberLoginUrl}/>}
				</div>}
			</ColumnMain>

			<ColumnSide className="hidden lg:block">
				<Summary offer={offer} startDate={parseStartDate(state?.startDate) ?? new Date()} discount={discount}/>
			</ColumnSide>
		</Layout>

		{/*<Dump value={pictureId}/>*/}
	</>)
}

export default Contact;
